import type { ConversationInfo } from "./serverChatStore";

/**
 * Pure grouping for the History tab. History is a reservoir of questions to relaunch, so it splits
 * into two groups only — no time dividers:
 * - `favourites`: every favourited question, most-recently-added on top (by `favouritedAt`);
 * - `recent`: the rest, deduped by identical question text (keeping the newest occurrence) and
 *   ordered by recency.
 */

/** ms since epoch for the most recent activity on a row (orders Recent), 0 when unknown. */
export function recencyMs(c: ConversationInfo): number {
	const iso = c.lastMessageTime ?? c.creationTime;
	return iso ? new Date(iso).getTime() : 0;
}

/** ms since epoch for when a row was favourited (orders Favourites), falling back to recency. */
export function favouritedMs(c: ConversationInfo): number {
	return c.favouritedAt ? new Date(c.favouritedAt).getTime() : recencyMs(c);
}

export type GroupedHistory = {
	favourites: ConversationInfo[];
	recent: ConversationInfo[];
};

export function groupHistory(list: ConversationInfo[]): GroupedHistory {
	const favourites = list.filter((c) => c.favourite).sort((a, b) => favouritedMs(b) - favouritedMs(a));

	// Collapse identical questions in Recent to their most recent occurrence.
	const byText = new Map<string, ConversationInfo>();
	for (const c of list) {
		if (c.favourite) {
			continue;
		}
		const key = (c.name ?? "").trim();
		const existing = byText.get(key);
		if (!existing || recencyMs(c) > recencyMs(existing)) {
			byText.set(key, c);
		}
	}
	const recent = [...byText.values()].sort((a, b) => recencyMs(b) - recencyMs(a));

	return { favourites, recent };
}
